import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ServiceCategory } from "@/data/servicesData";
import { getLocalizedAllCategories } from "@/lib/localizeServices";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { servicesCatalogCopy } from "@/lib/siteCopy";

function CategoryCard({ category }: { category: ServiceCategory }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const navigate = useNavigate();
  const lang = useSiteLanguage();
  const t = servicesCatalogCopy[lang];
  const Icon = category.icon;

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.45, ease: [0.23, 1, 0.32, 1] }}
      id={category.id}
      className="scroll-mt-28 flex flex-col rounded-2xl border border-border/70 bg-card/40 p-5 md:p-6 hover:border-primary/25 hover:bg-card/80 transition-colors duration-300"
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className={`w-10 h-10 rounded-xl bg-gradient-to-br ${category.gradient} flex items-center justify-center shrink-0`}
        >
          <Icon className="w-5 h-5 text-foreground/80" />
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold text-foreground leading-tight">
            {category.title}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
            {category.description}
          </p>
        </div>
      </div>

      <ul className="space-y-1.5 flex-1 mb-5">
        {category.services.map((service) => (
          <li key={service.id}>
            <Link
              to={`/services/${category.id}#${service.id}`}
              className="group flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 -mx-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
            >
              <span className="truncate">{service.title}</span>
              <ArrowRight className="w-3.5 h-3.5 shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </Link>
          </li>
        ))}
      </ul>

      <Button
        variant="outline"
        size="sm"
        className="w-full rounded-full justify-center"
        onClick={() => navigate(`/services/${category.id}`)}
      >
        {t.viewAll.replace("{count}", String(category.services.length))}
        <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </motion.article>
  );
}

export function ServicesCatalog() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const lang = useSiteLanguage();
  const t = servicesCatalogCopy[lang];
  const categories = getLocalizedAllCategories(lang);

  return (
    <section ref={ref} className="py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto mb-10 md:mb-12"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
            {t.eyebrow}
          </p>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-3">
            {t.title}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            {t.subtitle}
          </p>
        </motion.div>

        <motion.nav
          initial={{ opacity: 0, y: 8 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.45, delay: 0.1 }}
          aria-label={t.navLabel}
          className="flex flex-wrap justify-center gap-2 mb-10 md:mb-12"
        >
          {categories.map((category) => (
            <a
              key={category.id}
              href={`#${category.id}`}
              className="inline-flex items-center rounded-full border border-border/70 bg-background px-3.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:border-primary/30 hover:bg-muted/50 transition-colors"
            >
              {category.title}
            </a>
          ))}
        </motion.nav>

        <div className="grid sm:grid-cols-2 gap-4 md:gap-5 max-w-5xl mx-auto">
          {categories.map((category) => (
            <CategoryCard key={category.id} category={category} />
          ))}
        </div>
      </div>
    </section>
  );
}
